import { ToastOptions, toast } from "react-toastify";
import { addSeconds, addWeeks, format } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { dateToDateInputValue } from "../AppointmentsUtils";
import {
  AppointmentForm,
  BookAppointmentDefault,
} from "./BookAppointmentDrawer.types";
import {
  BookAppointmentMutationVariables,
  CreateAppointmentTemplateMutationVariables,
} from "./BookAppointmentDrawer-generated.hooks";

export function convertToBookAppointmentInput(
  values: AppointmentForm,
): BookAppointmentMutationVariables["input"] {
  return {
    appointmentTypeUuid: values.appointmentType.uuid,
    staffUuid: values.staff.userUuid,
    userUuid: values.member.uuid,
    timeRanges: values.when.map((item) => {
      const startTime = convertDateInputValueToIso(item.date);
      const endTime = addSeconds(
        new Date(startTime),
        values.appointmentType!.duration,
      ).toISOString();
      return {
        startTime,
        endTime,
      };
    }),
  };
}

export function convertToCreateTemplateInput(
  values: AppointmentForm,
  timezone: string,
): CreateAppointmentTemplateMutationVariables["input"] {
  const {
    appointmentType,
    staff,
    member,
    startDate,
    days,
    count,
    endDate,
    interval,
  } = values;
  return {
    template: {
      appointmentTypeUuid: appointmentType.uuid,
      staffUuid: staff.userUuid,
      userUuid: member.uuid,
      timezone,
      startDate: startDate ? startDate.split("T")[0] : undefined,
      description: `${member.name} - ${appointmentType.name}`,
    },
    options: {
      count,
      endDate,
      interval: interval.value,
      days: {
        su: days.su.enabled ? days.su.time : undefined,
        mo: days.mo.enabled ? days.mo.time : undefined,
        tu: days.tu.enabled ? days.tu.time : undefined,
        we: days.we.enabled ? days.we.time : undefined,
        th: days.th.enabled ? days.th.time : undefined,
        fr: days.fr.enabled ? days.fr.time : undefined,
        sa: days.sa.enabled ? days.sa.time : undefined,
      },
    },
  };
}

export function validateDays(days: AppointmentForm["days"]) {
  const hasSomeDayEnabled = Object.keys(days).some((key) => days[key].enabled);
  if (!hasSomeDayEnabled) {
    return "Select at least one day";
  }
  return null;
}

function convertDateInputValueToIso(value: string) {
  return `${value}:00.000Z`;
}

export const defaultToastOptions: ToastOptions = {
  position: "bottom-center",
  autoClose: 5000,
  hideProgressBar: true,
  closeOnClick: true,
  pauseOnHover: true,
  progress: undefined,
};

export function showSaveResult(fulfilledCount: number, rejectedCount: number) {
  if (rejectedCount === 0) {
    if (fulfilledCount === 1) {
      toast.success("1 appointment booked", defaultToastOptions);
    } else {
      toast.success(
        `${fulfilledCount} appointments booked`,
        defaultToastOptions,
      );
    }
  } else {
    if (rejectedCount === 1) {
      toast.error("1 appointment failed", defaultToastOptions);
    } else {
      toast.error(`${rejectedCount} appointments failed`, defaultToastOptions);
    }
  }
}

export function getNextWeek(date: string) {
  return format(addWeeks(new Date(date), 1), "yyyy-MM-dd'T'HH:mm");
}

export const intervalOptions = [
  {
    value: 1,
    label: "Repeat weekly",
  },
  {
    value: 2,
    label: "Repeat every 2 weeks",
  },
];

const days = ["su", "mo", "tu", "we", "th", "fr", "sa"];

export function getFormDefaultValues(
  defaultValues: BookAppointmentDefault,
): AppointmentForm {
  const { staffUuid, member, date } = defaultValues;
  const now = new Date();
  const nowRounded = roundHour(now);
  const dateValue = date
    ? formatInTimeZone(date, "UTC", "yyyy-MM-dd'T'HH:mm")
    : dateToDateInputValue(nowRounded);
  const timeValue = date
    ? formatInTimeZone(date, "UTC", "HH:mm")
    : format(nowRounded, "HH:mm");
  const weekDay = new Date(dateValue).getDay();
  const day = days[weekDay];

  return {
    staff: staffUuid
      ? {
          userUuid: staffUuid,
        }
      : undefined,
    when: [{ date: dateValue }],
    member: member,
    interval: intervalOptions[0],
    startDate: dateValue,
    days: {
      [day]: {
        enabled: true,
        time: timeValue,
      },
    },
  };
}

function roundHour(date: Date) {
  const result = date;
  if (result.getMinutes() > 0) {
    result.setMinutes(0);
    result.setHours(result.getHours() + 1);
  }
  return result;
}
